import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  QuestionMarkCircleIcon,
  TrashIcon,
  CheckIcon,
  PlusIcon,
} from '@heroicons/react/solid';
import { useTranslation } from 'react-i18next';
import convertToEditorState from '../../../../utils/convertToEditorState';
import covertEditorStateToJson from '../../../../utils/covertEditorStateToJson';
import TextEditor from '../../../../components/editor';

export function Preview(props) {
  const { selected } = props;
  const { t } = useTranslation();

  return (
    <div
      className={`h-full w-full text-sky mx-auto bg-grey-super_light rounded-md border-sky  ${
        selected ? 'opacity-100 border-3' : 'opacity-50 border-2'
      }`}
    >
      <QuestionMarkCircleIcon className="h-10 w-10 text-sky mx-auto mt-8" />
      <p>{t('courses.multiple_choice')}</p>
    </div>
  );
}

Preview.propTypes = {
  selected: PropTypes.bool.isRequired,
};

export function Panel(props) {
  const { item, setItem } = props;
  const { t } = useTranslation();
  const [editorState, setEditorState] = useState(
    convertToEditorState(item.content.explanation)
  );

  useEffect(() => {
    const newItem = { ...item };
    newItem.content.explanation = covertEditorStateToJson(editorState);
    setItem(newItem);
  }, [editorState]);

  const updateOption = (index, value) => {
    const old = item;
    const newContent = { ...old.content };
    const newOptions = [...old.content.options];
    newOptions[index] = value;
    newContent.options = newOptions;
    const newItem = { ...old, content: newContent };

    setItem(newItem);
  };

  const add = () => {
    const old = item;
    const newContent = { ...old.content };
    const newOptions = [...old.content.options, ''];
    newContent.options = newOptions;
    const newItem = { ...old, content: newContent };
    setItem(newItem);
  };

  const updateQuestion = (newQuestion) => {
    const old = item;
    const newItem = { ...old };
    newItem.content.question = newQuestion;
    setItem(newItem);
  };

  const removeAnswer = (indexToRemove) => {
    const old = item;
    const newContent = { ...old.content };
    const newOptions = [...old.content.options].filter(
      (_, index) => index !== indexToRemove
    );
    newContent.options = newOptions;
    const newItem = { ...old, content: newContent };
    setItem(newItem);
  };

  const setAnswer = (newAnswerIndex) => {
    const old = { ...item };
    const newContent = { ...old.content };
    if (newContent.answers.includes(newAnswerIndex)) {
      // Remove it from the list if its already in there
      newContent.answers = newContent.answers.filter(
        (e) => e !== newAnswerIndex
      );
    } else {
      // add it to the answers
      newContent.answers = [...newContent.answers, newAnswerIndex];
    }
    const newItem = { ...old, content: newContent };
    setItem(newItem);
  };

  return (
    <div className="w-full flex justify-center">
      <div className="flex flex-col space-y-5 max-w-xl w-full mt-8 rounded-lg shadow-md bg-white p-5">
        <div className="my-3 bg-gradient-to-br from-purple-500 via-sky-light to-sky-dark rounded-md shadow-md w-min text-white p-3">
          <QuestionMarkCircleIcon className="w-8" />
        </div>

        <span className="flex-col space-y-2">
          <label>{t('courses.open_question_textfield_label')}</label>
          <input
            type="text"
            value={item.content.question}
            onChange={(e) => updateQuestion(e.target.value)}
            placeholder={t('courses.type_question_here')}
            className="w-full rounded-md border-none outline-none bg-grey-super_light"
          />
        </span>
        <ul className="flex flex-col gap-y-4">
          {item.content.options.map((option, index) => (
            <li className="flex gap-4 items-center" key={index}>
              <Option
                option={option}
                index={index}
                update={updateOption}
                removeAnswer={removeAnswer}
                onClick={setAnswer}
                isAnswer={item.content.answers.includes(index)}
              />
            </li>
          ))}
        </ul>
        <div>
          <button
            type="button"
            onClick={add}
            className={`bg-sky hover:bg-sky-dark rounded-md text-white hover:shadow-md px-4 py-2 flex gap-2 ${
              item.content.options.length >= 6 ? 'bg-grey-medium' : ''
            }`}
            disabled={item.content.options.length >= 6}
          >
            <PlusIcon className="w-5 h-5" />
            {t('courses.add_answer')}
          </button>
        </div>
        {/* Explanation field */}
        <label>{t('courses.open_question_textfield_explanation')}</label>
        <TextEditor onChange={setEditorState} editorState={editorState} />
        {/* <span className="flex-col space-y-2">
          <label>{t('courses.open_question_textfield_explanation')}</label>
          <textarea
            className="w-full rounded-md border-none outline-none bg-grey-super_light"
            rows={5}
            placeholder={'explanation'}
            value={item.content.explanation}
            onChange={setExplanation}
          />
        </span> */}
      </div>
    </div>
  );
}

Panel.propTypes = {
  setItem: PropTypes.func.isRequired,
  item: PropTypes.object.isRequired,
};

const Option = (props) => {
  const { index, option, update, onClick, removeAnswer, isAnswer } = props;
  const { t } = useTranslation();
  return (
    <li
      type="button"
      className="w-full flex items-center justify-between gap-2 rounded-md bg-grey-super_light px-3 py-2"
    >
      <div className="flex space-x-3 items-center">
        {isAnswer ? (
          <button
            className="p-2 bg-sky hover:bg-sky-dark rounded-md"
            onClick={() => onClick(index)}
          >
            <CheckIcon className="w-5 rounded-md text-white" />
          </button>
        ) : (
          <button
            className="p-2 bg-grey-medium_light hover:bg-grey-medium rounded-md"
            onClick={() => onClick(index)}
          >
            <CheckIcon className="w-5 rounded-md text-white" />
          </button>
        )}
        <input
          placeholder={t('courses.type_answer_here')}
          type="text"
          value={option}
          onChange={(e) => {
            e.stopPropagation();
            update(index, e.target.value);
          }}
          className="w-full rounded-md border-none outline-none bg-grey-super_light"
        />
      </div>

      <button
        type="button"
        onClick={() => removeAnswer(index)}
        className="rounded-md text-grey-medium hover:text-grey-dark h-9 w-9 p-2 hover:textbg-red-700"
      >
        <TrashIcon className="w-6" />
      </button>
    </li>
  );
};

Option.propTypes = {
  option: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  isAnswer: PropTypes.bool.isRequired,
  update: PropTypes.func.isRequired,
  onClick: PropTypes.func.isRequired,
  removeAnswer: PropTypes.func.isRequired,
};
